import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

const MILESTONE = [
  (n) => `Five resets this week${n}. That's you choosing calm, again and again, and it's starting to show.`,
  (n) => `Five resets this week${n}. You're not waiting to feel steady any more. You're choosing it.`,
  (n) => `Five times this week you came back to yourself${n}. That's the rhythm we've been building.`,
];

// A quiet week view: days gently illuminate with engagement. No red, no missed-day guilt.
export default function MyRhythm({ week }) {
  const { user } = useAuth();
  if (!week) return null;
  const first = (user?.name || "").split(" ")[0];
  const wk = Math.floor(Date.now() / (7 * 864e5));
  const total = week.items.length || 1;
  const shown = week.days.filter((d) => d.completed.length > 0).length;
  const line = shown >= 5 ? `You showed up ${shown} times this week. Confidence is becoming something you practise rather than something you wait to feel.`
    : shown >= 2 ? `You showed up ${shown} times this week. Quietly, it's adding up.`
    : shown === 1 ? "You showed up this week. That's how it starts." : week.resets >= 5 ? "Your routine can wait. You've been looking after yourself in other ways." : "Whenever you're ready. The rhythm will be here.";
  return (
    <section className="mb-12" data-testid="routine-rhythm">
      <h2 className="text-xs uppercase tracking-[0.22em] text-foreground/90">My rhythm</h2>
      <p className="text-xs text-muted-foreground mb-6">You're building something quieter than a streak.</p>
      <div className="flex justify-between max-w-md">
        {week.days.map((d, i) => {
          const glow = Math.min(1, d.completed.length / Math.max(2, total * 0.6));
          return (
            <div key={d.date} className="flex flex-col items-center gap-2" data-testid={`rhythm-day-${d.date}`}>
              <motion.span initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }}
                className={`w-9 h-9 rounded-full ${d.is_today ? "ring-1 ring-primary/50" : ""}`}
                style={{ background: glow ? `radial-gradient(circle, rgba(150,190,240,${0.25 + glow * 0.6}) 0%, rgba(110,150,220,${glow * 0.25}) 55%, transparent 72%)` : "rgba(255,255,255,0.04)" }} />
              <span className={`text-[10px] ${d.is_today ? "text-foreground" : "text-muted-foreground"}`}>{d.weekday[0]}</span>
            </div>
          );
        })}
      </div>
      <p className="font-serif text-lg italic text-foreground/80 mt-6 max-w-lg" data-testid="rhythm-reflection">{line}</p>
      {week.resets >= 5 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 max-w-lg rounded-2xl border border-primary/30 bg-primary/[0.06] px-5 py-4" data-testid="rhythm-reset-milestone">
          <p className="text-[10px] uppercase tracking-[0.22em] text-primary mb-1.5">From your Coach</p>
          <p className="font-serif text-lg leading-snug">{MILESTONE[wk % MILESTONE.length](first ? `, ${first}` : "")}</p>
        </motion.div>
      )}
      {week.resets > 0 && week.resets < 5 && (
        <p className="text-sm text-muted-foreground mt-3 max-w-lg" data-testid="rhythm-resets">
          You reset {week.resets === 1 ? "once" : `${week.resets} times`} this week. Each one was a moment you chose calm.
        </p>
      )}
    </section>
  );
}
